import { Button, Modal, type ModalProps } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { prvApi } from "#/api/prvApi";
import { showErrorNotification, showSuccessNotification } from "#/components/notifications";
import { logger } from "#/utils/logger";
import { PRV_RUNS_QUERY_KEY } from "./PrvRunLogTable";

export interface Props extends ModalProps {
  clientId: number;
  clientName: string;
}

/**
 * Confirmation for the PRV clean slate. Same shape as the other destructive
 * confirmations on Client Detail: what happens, the affected subject in a
 * dashed box, what is kept, a bold question, Abort + red confirm.
 */
const PrvResetModal = ({ clientId, clientName, ...props }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const reset = useMutation({
    mutationFn: () => prvApi.resetData(clientId),
    onSuccess: (response) => {
      const removed = response.data;
      showSuccessNotification({
        message: t("clients.prv.reset.success", {
          runs: removed.runs,
          items: removed.itemStates,
          patterns: removed.patterns,
        }),
        color: "green.5",
      });
      void queryClient.invalidateQueries({ queryKey: [PRV_RUNS_QUERY_KEY, clientId] });
      void queryClient.invalidateQueries({ queryKey: ["prv-active-run", clientId] });
      void queryClient.invalidateQueries({ queryKey: ["prv-patterns", clientId] });
      props.onClose();
    },
    onError: (error: Error) => {
      // The API client already toasts the server message (e.g. 409 while running).
      logger.error("PrvResetModal reset error:", error);
      if (!error.message) showErrorNotification({ message: t("common.failedToProcess") });
    },
  });

  const consequences = [
    t("clients.prv.reset.consequences.runs"),
    t("clients.prv.reset.consequences.checkpoints"),
    t("clients.prv.reset.consequences.patterns"),
    t("clients.prv.reset.consequences.history"),
  ];
  const kept = [t("clients.prv.reset.kept.config"), t("clients.prv.reset.kept.targets")];

  return (
    <Modal
      {...props}
      centered
      title={t("clients.prv.reset.title")}
      closeOnClickOutside={false}
      onClose={() => props.onClose()}
      classNames={{ title: "font-bold" }}
    >
      <div className="flex w-full flex-col items-center justify-center gap-4">
        <div className="flex flex-col gap-2 text-sm">
          <span>{t("clients.prv.reset.description")}</span>

          <div className="mx-auto my-2 flex w-full flex-col gap-1 rounded-lg border border-dashed border-slate-400 bg-slate-100 p-4 text-center">
            <span className="font-bold">{clientName}</span>
            <span className="text-xs text-gray-600">{t("clients.prv.reset.subject")}</span>
          </div>

          <span className="font-semibold text-red-700">
            {t("clients.prv.reset.consequencesTitle")}
          </span>
          <ul className="list-disc space-y-1 pl-5">
            {consequences.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>

          <span className="mt-1 font-semibold">{t("clients.prv.reset.keptTitle")}</span>
          <ul className="list-disc space-y-1 pl-5 text-gray-700">
            {kept.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>

          <span className="my-2 font-bold">{t("clients.prv.reset.confirmation")}</span>
        </div>

        <div className="grid w-full grid-cols-[auto_1fr] items-center gap-2">
          <Button size="sm" color="black" onClick={() => props.onClose()}>
            {t("common.abort")}
          </Button>
          <Button
            onClick={() => reset.mutate()}
            color="red"
            size="sm"
            loading={reset.isPending}
            leftSection={<IconTrash size={18} />}
          >
            {t("clients.prv.reset.confirm")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PrvResetModal;
